/**
 * WebAuthn Hook for Passkey Registration and Authentication
 *
 * This hook provides client-side WebAuthn API integration for:
 * - Checking device passkey support
 * - Registering new passkeys (credentials)
 * - Authenticating with existing passkeys
 */

const WebAuthnHook = {
  mounted() {
    this.config = this.getConfig();

    // Check support on mount
    this.checkSupport();

    // Listen for server events
    this.handleEvent("webauthn:check-support", () => this.checkSupport());
    this.handleEvent("webauthn:register", (options) => this.register(options));
    this.handleEvent("webauthn:authenticate", (options) =>
      this.authenticate(options)
    );
  },

  getConfig() {
    const dataset = this.el?.dataset || {};
    const authRoutesPrefix = dataset.authRoutesPrefix || "/auth";
    const subject = dataset.authSubject || "user";
    const strategy = dataset.webauthnStrategy || "webauthn";

    return {
      authRoutesPrefix,
      subject,
      strategy,
      challengeUrl:
        dataset.webauthnChallengeUrl ||
        `${authRoutesPrefix}/${subject}/${strategy}/challenge`,
      signInUrl:
        dataset.webauthnSignInUrl ||
        `${authRoutesPrefix}/${subject}/${strategy}/sign_in`,
    };
  },

  /**
   * Check if the device supports WebAuthn/passkeys
   */
  async checkSupport() {
    const support = {
      webauthn: false,
      platformAuthenticator: false,
      conditionalUI: false,
      authenticatorType: null,
    };

    if (
      window.PublicKeyCredential &&
      typeof window.PublicKeyCredential === "function"
    ) {
      support.webauthn = true;

      try {
        support.platformAuthenticator =
          await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
      } catch (e) {
        console.warn("Could not check platform authenticator:", e);
      }

      try {
        if (PublicKeyCredential.isConditionalMediationAvailable) {
          support.conditionalUI =
            await PublicKeyCredential.isConditionalMediationAvailable();
        }
      } catch (e) {
        console.warn("Could not check conditional UI:", e);
      }

      support.authenticatorType = this.getAuthenticatorTypeLabel(support);
    }

    this.pushEventTo(this.el, "webauthn:support-result", support);
    return support;
  },

  /**
   * Get a user-friendly label for the authenticator type
   */
  getAuthenticatorTypeLabel(support) {
    if (!support.platformAuthenticator) {
      return "Security Key";
    }

    const ua = navigator.userAgent.toLowerCase();
    const platform = navigator.platform?.toLowerCase() || "";

    if (
      /iphone|ipad|ipod/.test(ua) ||
      (platform.includes("mac") && "ontouchend" in document)
    ) {
      return "Face ID";
    }
    if (platform.includes("mac")) {
      return "Touch ID";
    }
    if (/android/.test(ua)) {
      return "Fingerprint";
    }
    if (/win/.test(platform)) {
      return "Windows Hello";
    }

    return "Biometric";
  },

  /**
   * Register a new passkey
   */
  async register(options) {
    try {
      const challengeBuffer = this.base64urlToBuffer(options.challenge);
      const userIdBuffer = this.base64urlToBuffer(options.user.id);

      const publicKeyCredentialCreationOptions = {
        challenge: challengeBuffer,
        rp: {
          id: options.rp.id,
          name: options.rp.name,
        },
        user: {
          id: userIdBuffer,
          name: options.user.name,
          displayName: options.user.displayName,
        },
        pubKeyCredParams: options.pubKeyCredParams,
        timeout: options.timeout,
        authenticatorSelection: options.authenticatorSelection,
        attestation: options.attestation || "none",
      };

      if (options.excludeCredentials) {
        publicKeyCredentialCreationOptions.excludeCredentials =
          options.excludeCredentials.map((cred) => ({
            type: "public-key",
            id: this.base64urlToBuffer(cred.id),
            transports: cred.transports,
          }));
      }

      const credential = await navigator.credentials.create({
        publicKey: publicKeyCredentialCreationOptions,
      });

      const response = credential.response;

      let transports = [];
      if (response.getTransports) {
        transports = response.getTransports();
      }

      this.pushEventTo(this.el, "webauthn:register-success", {
        credential_id: this.bufferToBase64url(credential.rawId),
        attestation_object: this.bufferToBase64url(response.attestationObject),
        client_data_json: this.bufferToBase64url(response.clientDataJSON),
        transports: transports,
        authenticator_type: this.getAuthenticatorTypeLabel(
          await this.checkSupport()
        ),
      });
    } catch (error) {
      console.error("WebAuthn registration error:", error);

      let errorMessage = "Registration failed";
      let errorCode = "unknown";

      if (error.name === "NotAllowedError") {
        errorMessage = "Registration was cancelled or timed out";
        errorCode = "not_allowed";
      } else if (error.name === "InvalidStateError") {
        errorMessage = "A passkey already exists for this device";
        errorCode = "invalid_state";
      } else if (error.name === "NotSupportedError") {
        errorMessage = "This device does not support passkeys";
        errorCode = "not_supported";
      } else if (error.name === "SecurityError") {
        errorMessage = "Security error - please try again";
        errorCode = "security_error";
      }

      this.pushEventTo(this.el, "webauthn:register-error", {
        error: errorMessage,
        code: errorCode,
        details: error.message,
      });
    }
  },

  /**
   * Authenticate with an existing passkey
   * If options are provided, uses them directly.
   * If options are empty, fetches challenge from server first (for sign-in flow).
   */
  async authenticate(options) {
    try {
      let challengeOptions = options || {};
      const requestedContext = options?.context || options?.auth_context || "user";
      let authContext = requestedContext === "admin" ? "admin" : requestedContext;

      if (!challengeOptions.challenge) {
        challengeOptions = await this.fetchChallenge(authContext, options);
        authContext = challengeOptions.context || authContext;
      }

      const challengeBuffer = this.base64urlToBuffer(challengeOptions.challenge);

      const publicKeyCredentialRequestOptions = {
        challenge: challengeBuffer,
        rpId: challengeOptions.rpId,
        timeout: challengeOptions.timeout,
        userVerification: challengeOptions.userVerification || "preferred",
      };

      if (
        challengeOptions.allowCredentials &&
        challengeOptions.allowCredentials.length > 0
      ) {
        publicKeyCredentialRequestOptions.allowCredentials =
          challengeOptions.allowCredentials.map((cred) => ({
            type: "public-key",
            id: this.base64urlToBuffer(cred.id),
            transports: cred.transports,
          }));
      }

      const credential = await navigator.credentials.get({
        publicKey: publicKeyCredentialRequestOptions,
      });

      const response = credential.response;

      if (!options || !options.challenge) {
        const signInResult = await this.submitPasskeySignIn(
          credential,
          response,
          challengeOptions.challenge_key,
          authContext,
          options
        );

        if (signInResult.success) {
          window.location.href = signInResult.redirect_to || "/";
          return;
        } else {
          throw new Error(signInResult.error || "Sign-in failed");
        }
      }

      this.pushEventTo(this.el, "webauthn:authenticate-success", {
        credential_id: this.bufferToBase64url(credential.rawId),
        authenticator_data: this.bufferToBase64url(response.authenticatorData),
        client_data_json: this.bufferToBase64url(response.clientDataJSON),
        signature: this.bufferToBase64url(response.signature),
        user_handle: response.userHandle
          ? this.bufferToBase64url(response.userHandle)
          : null,
      });
    } catch (error) {
      console.error("WebAuthn authentication error:", error);

      let errorMessage = "Authentication failed";
      let errorCode = "unknown";

      if (error.name === "NotAllowedError") {
        errorMessage = "Authentication was cancelled or timed out";
        errorCode = "not_allowed";
      } else if (error.name === "SecurityError") {
        errorMessage = "Security error - please try again";
        errorCode = "security_error";
      } else if (error.message) {
        errorMessage = error.message;
      }

      this.pushEventTo(this.el, "webauthn:authenticate-error", {
        error: errorMessage,
        code: errorCode,
        details: error.message,
      });
    }
  },

  async fetchChallenge(context, options) {
    const challengeUrl = this.getChallengeUrl(options);
    const url = new URL(challengeUrl, window.location.origin);

    if (context) {
      url.searchParams.set("context", context);
    }

    if (options?.user_id) {
      url.searchParams.set("user_id", options.user_id);
    }

    const response = await fetch(url.toString(), {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
    });

    if (!response.ok) {
      throw new Error("Failed to get authentication challenge");
    }

    const data = await response.json();
    return data.options;
  },

  getChallengeUrl(options) {
    return (
      options?.challenge_url ||
      options?.challengeUrl ||
      this.config.challengeUrl
    );
  },

  getSignInUrl(options) {
    return (
      options?.sign_in_url ||
      options?.signInUrl ||
      this.config.signInUrl
    );
  },

  /**
   * Submit passkey sign-in credentials to the server
   */
  async submitPasskeySignIn(credential, response, challengeKey, context, options) {
    const signInUrl = this.getSignInUrl(options);

    const signInResponse = await fetch(signInUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document
          .querySelector('meta[name="csrf-token"]')
          ?.getAttribute("content"),
      },
      body: JSON.stringify({
        credential_id: this.bufferToBase64url(credential.rawId),
        authenticator_data: this.bufferToBase64url(response.authenticatorData),
        client_data_json: this.bufferToBase64url(response.clientDataJSON),
        signature: this.bufferToBase64url(response.signature),
        challenge_key: challengeKey,
        context: context,
        case_code: options?.case_code,
      }),
    });

    return await signInResponse.json();
  },

  base64urlToBuffer(base64url) {
    const padding = "=".repeat((4 - (base64url.length % 4)) % 4);
    const base64 = base64url.replace(/-/g, "+").replace(/_/g, "/") + padding;
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes.buffer;
  },

  bufferToBase64url(buffer) {
    const bytes = new Uint8Array(buffer);
    let binary = "";
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    const base64 = btoa(binary);
    return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
  },
};

export default WebAuthnHook;
